import axios from 'axios'
import { ref } from 'vue'
import { useToast } from 'vue-toast-notification'

export function useUsers({
    onArchived = () => {},
    onRestored = () => {},
    onCreated = () => {},
    onUpdated = () => {},
    onFetched = () => {},
    onError = () => {},
}) {
    const $toast = useToast()

    const archiving = ref(false)
    const restoring = ref(false)
    const creating = ref(false)
    const updating = ref(false)
    const fetching = ref(false)
    const isUploading = ref(false)
    const percentage = ref(0)
    const errors = ref(null)
    const user = ref(null)

    const buildUserFormData = (form) => {
        const formData = new FormData()

        Object.entries(form).forEach(([key, value]) => {
            if (key === 'centers') {
                return value.forEach((center) => {
                    formData.append(`${key}[]`, center.id)
                })
            }
            if (typeof value === 'boolean') {
                return formData.append(key, value ? 1 : 0)
            }
            if (value !== null && value !== '') {
                formData.append(key, value)
            }
        })

        return formData
    }

    const getUploadConfig = () => ({
        onUploadProgress: (progressEvent) => {
            percentage.value = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        },
    })

    const createUser = async (form) => {
        try {
            creating.value = true
            if (form.profile_picture !== null) {
                isUploading.value = true
            }

            const formData = buildUserFormData(form)
            await axios.post('/api/users', formData, getUploadConfig())

            $toast.success('Utilisateur ajouté avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            onCreated()
        } catch (error) {
            errors.value = error.response?.data?.errors
            onError(error)
        } finally {
            creating.value = false
            isUploading.value = false
        }
    }

    const updateUser = async (id, form) => {
        try {
            updating.value = true
            if (form.profile_picture !== null) {
                isUploading.value = true
            }

            const formData = buildUserFormData(form)
            formData.append('_method', 'PUT')
            await axios.post(`/api/users/${id}`, formData, getUploadConfig())

            $toast.success('Utilisateur modifié avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            onUpdated()
        } catch (error) {
            errors.value = error.response?.data?.errors
            onError(error)
        } finally {
            updating.value = false
            isUploading.value = false
        }
    }

    const fetchUser = async (id) => {
        if (!id) {
            console.warn('User ID is required.')
            return
        }
        try {
            fetching.value = true
            const response = await axios.get(`/api/users/${id}`)
            user.value = response.data
            onFetched(response.data.data)
        } catch (error) {
            onError(error)
        } finally {
            fetching.value = false
        }
    }

    const archiveUser = async (id) => {
        try {
            archiving.value = true
            await axios.delete(`/api/users/${id}`)
            onArchived()
        } catch (error) {
            onError(error)
        } finally {
            archiving.value = false
        }
    }

    const restoreUser = async (id) => {
        try {
            restoring.value = true
            await axios.post(`/api/users/${id}/restore`)
            $toast.success('Utilisateur restauré', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            onRestored()
        } catch (error) {
            onError(error)
        } finally {
            restoring.value = false
        }
    }

    return {
        archiving,
        restoring,
        creating,
        updating,
        fetching,
        isUploading,
        percentage,
        errors,
        user,
        archiveUser,
        restoreUser,
        createUser,
        updateUser,
        fetchUser,
    }
}
